import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

interface RyanAuthContextValue {
  hasRyanAccess: boolean;
  ryanAccessLoading: boolean;
  isVip: boolean;
  isEditor: boolean;
  isRepresentative: boolean;
  checkRyanAccess: () => Promise<void>;
}

const RyanAuthContext = createContext<RyanAuthContextValue>({
  hasRyanAccess: false,
  ryanAccessLoading: true,
  isVip: false,
  isEditor: false,
  isRepresentative: false,
  checkRyanAccess: async () => {},
});

export const RyanAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [hasRyanAccess, setHasRyanAccess] = useState(false);
  const [ryanAccessLoading, setRyanAccessLoading] = useState(true);
  const [isVip, setIsVip] = useState(false);
  const [isEditor, setIsEditor] = useState(false);
  const [isRepresentative, setIsRepresentative] = useState(false);

  const checkRyanAccess = useCallback(async () => {
    setRyanAccessLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setHasRyanAccess(false);
        setIsVip(false);
        setIsEditor(false);
        setIsRepresentative(false);
        return;
      }
      const res = await fetch('/api/ryan/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setHasRyanAccess(false);
        setIsVip(false);
        setIsEditor(false);
        setIsRepresentative(false);
        return;
      }
      const data = await res.json();
      setHasRyanAccess(data.hasAccess === true);
      setIsVip(data.isVip === true);
      setIsEditor(data.isEditor === true);
      setIsRepresentative(data.isRepresentative === true);
    } catch {
      setHasRyanAccess(false);
      setIsVip(false);
      setIsEditor(false);
      setIsRepresentative(false);
    } finally {
      setRyanAccessLoading(false);
    }
  }, []);

  useEffect(() => {
    checkRyanAccess();
  }, [checkRyanAccess]);

  return (
    <RyanAuthContext.Provider value={{ hasRyanAccess, ryanAccessLoading, isVip, isEditor, isRepresentative, checkRyanAccess }}>
      {children}
    </RyanAuthContext.Provider>
  );
};

export const useRyanAuth = () => useContext(RyanAuthContext);
